import { FC, useEffect, useRef, useState } from 'react';
import useRuntime from '@belong/providers/runtime';
import { COLOURS } from '@belong/themes';
import { ICON_NAMES, INavLinkProps } from '@belong/types';
import { Icon } from '../../Icon';
import { Container } from '../../layout/Container';
import { Button, CurrentItem, Link, List, ListItem, Root } from './Breadcrumbs.styles';

interface IProps {
  links: INavLinkProps[];
}

const Breadcrumbs: FC<IProps> = ({ links }: IProps) => {
  const { isMobileViewport } = useRuntime();
  const [showEllipsis, setShowEllipsis] = useState(isMobileViewport && links.length >= 5);
  const [keyboardTriggeredEvent, setKeyboardTriggeredEvent] = useState(false);
  const wrapperRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (wrapperRef.current && !showEllipsis && keyboardTriggeredEvent) {
      const listItems = Array.from(wrapperRef.current.querySelectorAll('li'));
      const interactiveElements = listItems.map(li => li.querySelector<HTMLElement>('a'));
      interactiveElements[1]?.focus();
    }
  }, [showEllipsis, keyboardTriggeredEvent]);

  if (isMobileViewport && links.length > 3) {
    const { label: firstElementLabel, ...firstElementProps } = links[0];
    const { label: lastElementLabel } = links[links.length - 1];
    const extractedElements = links.slice(1, links.length - 1);

    return (
      <Root data-testid="global-header-breadcrumbs" aria-label="Breadcrumb" ref={wrapperRef}>
        <Container>
          <List>
            <ListItem>
              <Link {...firstElementProps}>{firstElementLabel}</Link>
              <Icon name={ICON_NAMES.ChevronRight} aria-hidden />
            </ListItem>

            {showEllipsis ? (
              <ListItem>
                <Button
                  label="..."
                  a11yLabel="Show more breadcrumbs"
                  variant="quaternary"
                  onClick={event => {
                    setShowEllipsis(false);
                    // focus is set only when navigated via keyboard
                    // we detect this by checking the element the event is coming from is also the activeElement
                    // i.e. event.currentTarget === document.activeElement
                    setKeyboardTriggeredEvent(event.currentTarget === document.activeElement);
                  }}
                  color={COLOURS.BELONG_BLUE_LIGHT}
                />
                <Icon name={ICON_NAMES.ChevronRight} aria-hidden />
              </ListItem>
            ) : (
              extractedElements.map(link => {
                const { label, ...linkProps } = link;
                return (
                  <ListItem key={label}>
                    <Link {...linkProps}>{label}</Link>
                    <Icon name={ICON_NAMES.ChevronRight} aria-hidden />
                  </ListItem>
                );
              })
            )}
            <ListItem>
              <CurrentItem aria-current="page">{lastElementLabel}</CurrentItem>
            </ListItem>
          </List>
        </Container>
      </Root>
    );
  }

  return (
    <Root data-testid="global-header-breadcrumbs" aria-label="Breadcrumb">
      <Container>
        <List>
          {links.map((link, idx, arr) => {
            const isLast = idx === arr.length - 1;
            const { label, ...linkProps } = link;

            return (
              <ListItem key={label}>
                {isLast ? (
                  <CurrentItem aria-current="page">{label}</CurrentItem>
                ) : (
                  <>
                    <Link {...linkProps}>{label}</Link>
                    <Icon name={ICON_NAMES.ChevronRight} aria-hidden />
                  </>
                )}
              </ListItem>
            );
          })}
        </List>
      </Container>
    </Root>
  );
};

Breadcrumbs.displayName = 'Breadcrumbs';

export default Breadcrumbs;
